import { Button } from "reactstrap"
import { autoTable } from "jspdf-autotable"
import formatMileage from "utils/formatMileage"
import apiClient from "api/axios"
import { useState } from "react"
import jsPDF from "jspdf"
import commingSoonImg from "../../../../../../../assets/images/photo-coming-soon.jpg"
import logoLight from "../../../../../../../assets/images/logo.png"
import { formatReadableDate } from "utils/formatDate"
import CarStockImporter, { getCarStockImporterText } from "./CarStockImporter"

const CarStockList = ({
  btn = { className: "border-0", size: "sm", color: "primary" },
  renderTrigger,
  ...params
}) => {
  const [loading, setLoading] = useState(false)
  const [groupStocks, setGroupStocks] = useState(null)

  // PDF Configuration Constants
  const PDF_CONFIG = {
    pageSize: "a4",
    orientation: "landscape",
    unit: "pt",
    margins: {
      left: 30,
      right: 30,
      top: 40,
      bottom: 40,
    },
    colors: {
      primary: [0, 102, 204],
      secondary: [102, 102, 102],
      accent: [41, 128, 185],
      lightGray: [245, 245, 245],
      white: [255, 255, 255],
      black: [51, 51, 51],
      border: [221, 221, 221],
      success: [40, 167, 69],
      warning: [255, 193, 7],
      danger: [220, 53, 69],
    },
  }

  const handleDownloadPdf = async () => {
    try {
      setLoading(true)

      // ✅ Call API only when button is clicked
      const response = await apiClient.get("/vehicle-stocks/pdf", {
        params,
      })

      const groups = response?.data?.data?.groups
      if (!groups) return

      setGroupStocks(groups)

      // ✅ Generate PDF after API response
      await generatePdf(groups)
    } catch (error) {
      console.error("Failed to fetch stocks:", error)
    } finally {
      setLoading(false)
    }
  }
  const generatePdf = async groups => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    })

    const totalVehicles = calculateTotalVehicles(groups)

    let y = 40

    y = await renderCompanyHeader(doc, y)
    y = renderMainTitle(doc, y)

    await renderStockGroups(doc, y, groups)

    renderFooter(doc, totalVehicles)

    doc.save(`CarStockList_${new Date().toISOString().split("T")[0]}.pdf`)
  }

  const renderCompanyHeader = async (doc, startY) => {
    let y = startY

    // Company Header with full width
    doc.setFont("helvetica", "bold")
    doc.setFontSize(28)
    doc.setTextColor(0, 51, 102)
    doc.text("AUROBIL CARS", PDF_CONFIG.margins.left, y)

    // --- Company Logo (Right) ---
    try {
      const logoData = await loadImageAsBase64(logoLight, 200, 200)

      if (logoData.hasImage) {
        const logoWidth = 130 // Adjust logo size
        const logoHeight = 100 // Adjust height proportionally
        const logoX =
          doc.internal.pageSize.width - PDF_CONFIG.margins.right - logoWidth
        const logoY = y - 25 // Adjust vertical alignment with text

        doc.addImage(
          logoData.base64,
          "PNG",
          logoX,
          logoY,
          logoWidth,
          logoHeight,
          undefined,
          "FAST"
        )
      }
    } catch (e) {
      console.error("Error loading logo:", e)
      // optionally ignore or show a placeholder
    }

    // Tagline - full text
    y += 30
    doc.setFontSize(12)
    doc.setFont("helvetica", "normal")
    doc.setTextColor(
      PDF_CONFIG.colors.secondary[0],
      PDF_CONFIG.colors.secondary[1],
      PDF_CONFIG.colors.secondary[2]
    )
    doc.text(
      "BRAND NEW & RE-CONDITION VEHICLES IMPORTER & RETAILER",
      PDF_CONFIG.margins.left,
      y
    )

    // Address Block - full text with proper spacing
    y += 25
    doc.setFontSize(10)
    doc.setTextColor(
      PDF_CONFIG.colors.black[0],
      PDF_CONFIG.colors.black[1],
      PDF_CONFIG.colors.black[2]
    )

    const addresses = [
      `Showroom 1: 279/6/B, ECB Chattar, Dhaka-1206`,
      `Showroom 2: Ka-42/4, Pragati Sarani, Dhaka-1229`,
      `Contact: 01911-887731 | Email: aurobilcars@gmail.com | Web: www.aurobilcars.com`,
      `Showroom Hours: 10:00 AM – 8:00 PM, Closed: Friday`,
    ]

    addresses.forEach(address => {
      doc.text(address, PDF_CONFIG.margins.left, y)
      y += 16
    })

    // Decorative Line
    y += 10
    doc.setDrawColor(
      PDF_CONFIG.colors.primary[0],
      PDF_CONFIG.colors.primary[1],
      PDF_CONFIG.colors.primary[2]
    )
    doc.setLineWidth(1.5)
    doc.line(
      PDF_CONFIG.margins.left,
      y,
      doc.internal.pageSize.width - PDF_CONFIG.margins.right,
      y
    )

    return y + 30
  }

  const renderMainTitle = (doc, startY) => {
    let y = startY

    doc.setFont("helvetica", "bold")
    doc.setFontSize(18)
    doc.setTextColor(
      PDF_CONFIG.colors.primary[0],
      PDF_CONFIG.colors.primary[1],
      PDF_CONFIG.colors.primary[2]
    )
    doc.text(
      "COMPLETE STOCK - READY & UPCOMING UNITS",
      PDF_CONFIG.margins.left,
      y
    )

    y += 20
    doc.setFontSize(11)
    doc.setFont("helvetica", "normal")
    doc.setTextColor(
      PDF_CONFIG.colors.secondary[0],
      PDF_CONFIG.colors.secondary[1],
      PDF_CONFIG.colors.secondary[2]
    )

    // Add generation details
    const dateStr = `Generated on: ${formatReadableDate(new Date())}`

    doc.text(dateStr, PDF_CONFIG.margins.left, y)

    // Subtitle Line
    y += 20
    doc.setDrawColor(
      PDF_CONFIG.colors.border[0],
      PDF_CONFIG.colors.border[1],
      PDF_CONFIG.colors.border[2]
    )
    doc.setLineWidth(0.5)
    doc.line(
      PDF_CONFIG.margins.left,
      y,
      doc.internal.pageSize.width - PDF_CONFIG.margins.right,
      y
    )

    return y + 25
  }

  const loadImageAsBase64 = (url, imgWidth = 80, imgHeight = 50) => {
    return new Promise(resolve => {
      const img = new Image()
      img.crossOrigin = "https://app.aurobilcars.com"

      img.onload = () => {
        const canvas = document.createElement("canvas")
        const ctx = canvas.getContext("2d")

        canvas.width = imgWidth
        canvas.height = imgHeight

        // Clear canvas to transparent before drawing
        ctx.clearRect(0, 0, imgWidth, imgHeight)

        // Draw image with proper aspect ratio
        ctx.drawImage(img, 0, 0, imgWidth, imgHeight)

        // Preserve transparency in PNG
        const base64 = canvas.toDataURL("image/png")

        resolve({
          base64: base64,
          width: imgWidth,
          height: imgHeight,
          hasImage: true,
        })
      }

      img.onerror = () => {
        // Return placeholder on error
        resolve({
          base64: null,
          width: 0,
          height: 0,
          hasImage: false,
        })
      }

      img.src = url
    })
  }

  const prepareTableData = async cars => {
    const tableData = []

    for (let i = 0; i < cars.length; i++) {
      const car = cars[i]

      // Step 1: Set the actual image URL
      let imageUrl = car.primary_image || null

      // Step 2: Try loading the image
      let imageData = await loadImageAsBase64(imageUrl)

      // Step 3: If image fails, use "Coming Soon" placeholder
      if (!imageData.hasImage) {
        imageData = await loadImageAsBase64(
          commingSoonImg // Local placeholder or public URL
        )
      }

      tableData.push({
        slNo: i + 1,
        stockId: car.stock_id || "N/A",
        chassis: getCarStockImporterText(car, () => true),
        year: car.year || "N/A",
        color: car.color || "N/A",
        package: car.package || "N/A",
        grade: `${car.grade ?? "N/A"} / ${
          car.mileage ? formatMileage(car.mileage, "km", "-", true) : "N/A"
        }`,
        location: car.location || "N/A",
        status: formatStatus(car.status),
        photo: imageData,
        googleDriveLink: car?.google_drive_link || car?.website_link || "#",
        websiteLink: car?.website_link || car?.google_drive_link || "#",
      })
    }

    return tableData
  }

  const formatStatus = status => {
    if (!status) return "N/A"
    const statusMap = {
      available: "✓ AVAILABLE",
      sold: "✗ SOLD",
      upcoming: "⟳ UPCOMING",
      reserved: "◉ RESERVED",
      in_transit: "⚡ IN TRANSIT",
      processing: "⏳ PROCESSING",
    }
    return statusMap[status.toLowerCase()] || status
  }

  const getStatusColor = status => {
    if (!status) return PDF_CONFIG.colors.black
    const statusLower = status.toLowerCase()
    if (statusLower === "available") return PDF_CONFIG.colors.success
    if (statusLower === "sold") return PDF_CONFIG.colors.danger
    if (statusLower === "upcoming") return PDF_CONFIG.colors.warning
    if (statusLower === "reserved") return PDF_CONFIG.colors.accent
    return PDF_CONFIG.colors.black
  }

  const calculateTotalVehicles = groups => {
    return Object.values(groups).reduce(
      (total, vehicles) => total + vehicles.length,
      0
    )
  }

  const renderStockGroups = async (doc, startY, groups) => {
    let currentY = startY
    const groupEntries = Object.entries(groups)
    const totalVehicles = calculateTotalVehicles(groups)

    if (totalVehicles === 0) {
      // Show friendly message if no vehicles are available
      doc.setFont("helvetica", "bold")
      doc.setFontSize(14)
      doc.setTextColor(
        PDF_CONFIG.colors.primary[0],
        PDF_CONFIG.colors.primary[1],
        PDF_CONFIG.colors.primary[2]
      )
      doc.text(
        "Oops! We currently have no vehicles in stock.",
        PDF_CONFIG.margins.left,
        currentY
      )

      doc.setFont("helvetica", "normal")
      doc.setFontSize(12)
      doc.setTextColor(
        PDF_CONFIG.colors.secondary[0],
        PDF_CONFIG.colors.secondary[1],
        PDF_CONFIG.colors.secondary[2]
      )
      currentY += 20
      doc.text(
        "But don't worry – we are always importing new vehicles. Contact us today to find your dream ride!",
        PDF_CONFIG.margins.left,
        currentY
      )

      return currentY + 40 // skip table rendering
    }

    // Render Summary Card (same as before)
    doc.setFillColor(240, 248, 255)
    doc.rect(
      PDF_CONFIG.margins.left,
      currentY - 15,
      doc.internal.pageSize.width -
        (PDF_CONFIG.margins.left + PDF_CONFIG.margins.right),
      50,
      "F"
    )

    doc.setFont("helvetica", "bold")
    doc.setFontSize(12)
    doc.setTextColor(
      PDF_CONFIG.colors.primary[0],
      PDF_CONFIG.colors.primary[1],
      PDF_CONFIG.colors.primary[2]
    )
    doc.text(
      `TOTAL: ${totalVehicles} VEHICLES`,
      PDF_CONFIG.margins.left + 10,
      currentY
    )

    doc.setFont("helvetica", "normal")
    doc.setFontSize(10)
    doc.setTextColor(
      PDF_CONFIG.colors.black[0],
      PDF_CONFIG.colors.black[1],
      PDF_CONFIG.colors.black[2]
    )
    doc.text(
      `We have ${groupEntries.length} vehicle groups waiting for you – pick your dream ride today!`,
      PDF_CONFIG.margins.left + 10,
      currentY + 20
    )

    currentY += 50

    for (const [groupName, vehicles] of groupEntries) {
      // Check if we need a new page
      if (currentY > doc.internal.pageSize.height - 150) {
        doc.addPage()
        currentY = PDF_CONFIG.margins.top
      }

      // Group Header (same as before)
      doc.setFillColor(
        PDF_CONFIG.colors.primary[0],
        PDF_CONFIG.colors.primary[1],
        PDF_CONFIG.colors.primary[2]
      )
      doc.rect(
        PDF_CONFIG.margins.left,
        currentY - 20,
        doc.internal.pageSize.width -
          (PDF_CONFIG.margins.left + PDF_CONFIG.margins.right),
        25,
        "F"
      )

      doc.setFont("helvetica", "bold")
      doc.setFontSize(14)
      doc.setTextColor(
        PDF_CONFIG.colors.white[0],
        PDF_CONFIG.colors.white[1],
        PDF_CONFIG.colors.white[2]
      )
      doc.text(groupName, PDF_CONFIG.margins.left + 10, currentY - 5)

      // Vehicle Count Badge
      doc.setFillColor(255, 255, 255) // White fill
      doc.setDrawColor(
        PDF_CONFIG.colors.primary[0],
        PDF_CONFIG.colors.primary[1],
        PDF_CONFIG.colors.primary[2]
      ) // Border color
      doc.setLineWidth(1.5) // Adjust thickness if needed

      const countText = `${vehicles.length} Vehicle${
        vehicles.length !== 1 ? "s" : ""
      }`
      const countWidth = doc.getTextWidth(countText) + 30

      // Draw rectangle with both fill and stroke (border)
      doc.rect(
        doc.internal.pageSize.width -
          PDF_CONFIG.margins.right -
          countWidth -
          10, // x
        currentY - 25, // y
        countWidth, // width
        25, // height
        "FD" // F = fill, D = stroke (border)
      )

      // Set text color
      doc.setTextColor(
        PDF_CONFIG.colors.primary[0],
        PDF_CONFIG.colors.primary[1],
        PDF_CONFIG.colors.primary[2]
      )

      // Draw the text inside the badge
      doc.text(
        countText,
        doc.internal.pageSize.width -
          PDF_CONFIG.margins.right -
          countWidth +
          15,
        currentY - 8
      )

      // Reset text color for the rest
      doc.setTextColor(
        PDF_CONFIG.colors.black[0],
        PDF_CONFIG.colors.black[1],
        PDF_CONFIG.colors.black[2]
      )

      currentY += 15

      // Prepare table data with images
      const tableBody = await prepareTableData(vehicles)

      // Convert to array format for autoTable
      const tableRows = tableBody.map(item => [
        item.slNo,
        item.stockId,
        item.chassis,
        item.year,
        item.color,
        item.package,
        item.grade,
        item.location,
        item.status,
        item.photo,
        item.googleDriveLink,
        item.websiteLink,
      ])

      // Calculate column widths for full-width table
      const pageWidth =
        doc.internal.pageSize.width -
        PDF_CONFIG.margins.left -
        PDF_CONFIG.margins.right

      // Table Options
      const tableOptions = {
        startY: currentY,
        head: [
          [
            "SL",
            "Stock ID",
            "Chassis Number",
            "Year",
            "Color",
            "Package",
            "Grade",
            "Location",
            "Status",
            "Photo",
            "Google Drive",
            "Website",
          ],
        ],
        body: tableRows,
        theme: "grid",
        headStyles: {
          fillColor: PDF_CONFIG.colors.secondary,
          textColor: PDF_CONFIG.colors.white,
          halign: "center",
          fontStyle: "bold",
          fontSize: 10,
          cellPadding: 8,
          minCellHeight: 25,
        },
        bodyStyles: {
          fontSize: 9,
          halign: "center",
          textColor: PDF_CONFIG.colors.black,
          cellPadding: { top: 4, right: 6, bottom: 4, left: 6 },
          valign: "middle",
        },
        alternateRowStyles: {
          fillColor: PDF_CONFIG.colors.lightGray,
        },
        columnStyles: {
          0: { cellWidth: pageWidth * 0.05, halign: "center" },
          1: { cellWidth: pageWidth * 0.08, halign: "center" },
          2: { cellWidth: pageWidth * 0.11, halign: "left" },
          3: { cellWidth: pageWidth * 0.05, halign: "center" },
          4: { cellWidth: pageWidth * 0.08, halign: "center" },
          5: { cellWidth: pageWidth * 0.1, halign: "center" },
          6: { cellWidth: pageWidth * 0.1, halign: "center" },
          7: { cellWidth: pageWidth * 0.1, halign: "center" },
          8: { cellWidth: pageWidth * 0.08, halign: "center" },
          9: {
            cellWidth: pageWidth * 0.09,
            halign: "center",
            minCellHeight: 30,
          },
          10: { cellWidth: pageWidth * 0.08, halign: "center" },
          11: { cellWidth: pageWidth * 0.08, halign: "center" },
        },
        margin: {
          left: PDF_CONFIG.margins.left,
          right: PDF_CONFIG.margins.right,
        },
        styles: {
          font: "helvetica",
          lineColor: PDF_CONFIG.colors.border,
          lineWidth: 0.1,
          overflow: "linebreak",
          cellWidth: "wrap",
        },
        didParseCell: data => {
          // Color code status cells
          if (
            data.section === "body" &&
            data.column.index === 8 &&
            data.cell.raw
          ) {
            const status = data.cell.raw
            const cleanStatus = status.replace(/[✓✗⟳◉⚡⏳]/g, "").trim()
            const statusColor = getStatusColor(cleanStatus)

            data.cell.styles.textColor = statusColor
          }
          if (data.section === "body") {
            // Clear text in photo column - we'll draw images manually
            if (data.column.index === 9) {
              data.cell.text = [] // Remove any text from photo cell
            }

            // Clear text in link columns - we'll draw custom display text
            if (data.column.index === 10 || data.column.index === 11) {
              data.cell.text = [] // Remove URL text
            }
          }
        },
        didDrawCell: data => {
          if (data.section === "body") {
            // Draw image in photo column
            if (data.column.index === 9) {
              const imageData = data.cell.raw

              if (imageData && imageData.hasImage && imageData.base64) {
                try {
                  const cellX = data.cell.x
                  const cellY = data.cell.y
                  const cellWidth = data.cell.width
                  const cellHeight = data.cell.height

                  // Calculate image position (centered in cell with some padding)
                  const imgWidth = 25
                  const imgHeight = 25

                  const x = cellX + (cellWidth - imgWidth) / 2
                  const y = cellY + (cellHeight - imgHeight) / 2

                  // Add image to PDF
                  doc.addImage(
                    imageData.base64,
                    "JPEG",
                    x,
                    y,
                    imgWidth,
                    imgHeight,
                    undefined,
                    "FAST"
                  )
                } catch (e) {
                  console.error("Error adding image to PDF:", e)
                  // Add "No Image" text if image fails
                  doc.setFontSize(8)
                  doc.setTextColor(150, 150, 150)
                  doc.text(
                    "No Image",
                    data.cell.x + data.cell.width / 2,
                    data.cell.y + data.cell.height / 2,
                    { align: "center" }
                  )
                }
              } else {
                // Add "No Image" placeholder text
                doc.setFontSize(8)
                doc.setTextColor(150, 150, 150)
                doc.text(
                  "No Image",
                  data.cell.x + data.cell.width / 2,
                  data.cell.y + data.cell.height / 2,
                  { align: "center" }
                )
              }
            }

            // Render links with display text only (no URLs)
            if (data.column.index === 10 || data.column.index === 11) {
              const url = data.cell.raw
              if (url) {
                // Set display text based on column
                const displayText = data.column.index === 10 ? "Drive" : "Web"
                const x = data.cell.x + data.cell.width / 2
                const y = data.cell.y + data.cell.height / 2 + 3

                // Draw clickable link text
                doc.setFont("helvetica", "normal")
                doc.setTextColor(0, 0, 255)
                doc.text(displayText, x, y, { align: "center" })

                // Create clickable link area
                const textWidth = doc.getTextWidth(displayText)
                doc.link(x - textWidth / 2, y - 8, textWidth, 10, {
                  url,
                  newWindow: true,
                })

                // Reset font
                doc.setFont("helvetica", "normal")
                doc.setTextColor(
                  PDF_CONFIG.colors.black[0],
                  PDF_CONFIG.colors.black[1],
                  PDF_CONFIG.colors.black[2]
                )
              } else {
                // No link available
                doc.setFontSize(8)
                doc.setTextColor(150, 150, 150)
                doc.text(
                  "N/A",
                  data.cell.x + data.cell.width / 2,
                  data.cell.y + data.cell.height / 2 + 3,
                  { align: "center" }
                )
              }
            }
          }
        },
        didDrawPage: data => {
          // Add continuation header
          if (data.pageNumber > 1) {
            doc.setFontSize(9)
            doc.setTextColor(
              PDF_CONFIG.colors.secondary[0],
              PDF_CONFIG.colors.secondary[1],
              PDF_CONFIG.colors.secondary[2]
            )
            doc.setFont("helvetica", "italic")
            doc.text(
              `${groupName} (Continued from previous page)`,
              PDF_CONFIG.margins.left,
              PDF_CONFIG.margins.top - 10
            )
          }
        },
      }

      autoTable(doc, tableOptions)

      // Add spacing between groups
      const lastY = doc.lastAutoTable.finalY || currentY
      currentY = lastY + 40
    }

    return currentY
  }

  const renderFooter = (doc, totalVehicles) => {
    const pageCount = doc.getNumberOfPages()

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)

      // Footer line
      doc.setDrawColor(
        PDF_CONFIG.colors.border[0],
        PDF_CONFIG.colors.border[1],
        PDF_CONFIG.colors.border[2]
      )
      doc.setLineWidth(0.5)
      doc.line(
        PDF_CONFIG.margins.left,
        doc.internal.pageSize.height - 35,
        doc.internal.pageSize.width - PDF_CONFIG.margins.right,
        doc.internal.pageSize.height - 35
      )

      // Left footer - Company info
      doc.setFontSize(8)
      doc.setTextColor(
        PDF_CONFIG.colors.primary[0],
        PDF_CONFIG.colors.primary[1],
        PDF_CONFIG.colors.primary[2]
      )
      doc.setFont("helvetica", "bold")
      doc.text(
        "AUROBIL CARS - Premium Vehicle Importer & Retailer",
        PDF_CONFIG.margins.left,
        doc.internal.pageSize.height - 20
      )

      // Center footer - Summary
      doc.setFont("helvetica", "normal")
      doc.setTextColor(
        PDF_CONFIG.colors.secondary[0],
        PDF_CONFIG.colors.secondary[1],
        PDF_CONFIG.colors.secondary[2]
      )
      const summaryText = `${
        totalVehicles > 0 ? `Total Vehicles: ${totalVehicles} | ` : ``
      }Generated: ${formatReadableDate(new Date())}`
      const summaryWidth = doc.getTextWidth(summaryText)
      doc.text(
        summaryText,
        (doc.internal.pageSize.width - summaryWidth) / 2,
        doc.internal.pageSize.height - 20
      )

      // Right footer - Page numbers
      doc.setTextColor(
        PDF_CONFIG.colors.secondary[0],
        PDF_CONFIG.colors.secondary[1],
        PDF_CONFIG.colors.secondary[2]
      )
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width - PDF_CONFIG.margins.right,
        doc.internal.pageSize.height - 20,
        { align: "right" }
      )
    }
  }

  return renderTrigger ? (
    renderTrigger({ onDownload: handleDownloadPdf, loading })
  ) : (
    <Button
      onClick={handleDownloadPdf}
      disabled={loading}
      {...btn}
      style={{ boxShadow: "none" }}
    >
      <div className="d-flex align-items-center gap-1">
        {loading ? (
          <>
            <span className="spinner-border spinner-border-sm" />
            <span className="ms-2">Generating...</span>
          </>
        ) : (
          <>
            <i className="bx bx-download font-size-16" />
            <span className="d-none d-sm-inline">Download PDF</span>
          </>
        )}
      </div>
    </Button>
  )
}

export default CarStockList
